import { useState, useRef, useEffect } from "react";
import { UseFormWatch, UseFormSetValue } from "react-hook-form";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

function computeBrightness(img: HTMLImageElement): Promise<number> {
  return new Promise((resolve, reject) => {
    // Wait until image has valid dimensions
    if (!img.naturalWidth || !img.naturalHeight) {
      img.onload = () => computeBrightness(img).then(resolve).catch(reject);
      return;
    }

    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject("Canvas context not available");

      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      ctx.drawImage(img, 0, 0);

      const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      let total = 0;
      const count = data.length / 4;

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        total += 0.299 * r + 0.587 * g + 0.114 * b; // perceived brightness
      }

      const avg = total / count;
      resolve(avg);
    } catch (err) {
      reject(err);
    }
  });
}

function updateNavContrast(img: HTMLImageElement, root: HTMLElement | null) {
  console.log("updateNavContrast called", img?.src, root);
  if (!root) return;

  if (!img.complete) {
    img.onload = () => updateNavContrast(img, root);
    return;
  }

  //ensure cross-origin images are handled
  img.crossOrigin = "anonymous";

  computeBrightness(img).then((brightness) => {
    console.log("brightness", brightness);
    if (brightness > 182) {
      root.classList.add("nav-dark");
      root.classList.remove("nav-light");
    } else {
      root.classList.add("nav-light");
      root.classList.remove("nav-dark");
    }
  });
}

interface LeftPaneMobileProps {
  classMeta: {
    name: string;
    displayName?: string;
    shortDescription: string;
    sections: Record<string, string>;
    imageUrl?: string;
  };
  pricing: {
    pricing: string;
  };
  suiteImages?: { src: string; alt?: string }[];
  isRoomMode: boolean;
  isFixedMode: boolean;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  availableClassNames: string[];
  availableDurations: string[];
  onClassChange?: (newClassName: string) => void;
  selectedWindow?: any;
  classDurationMap: Record<string, string>;
  suiteProductIds: { productId: string; productName: string }[];
  isSpecificFixedClassPage?: boolean;
}

export default function LeftPaneMobile({
  classMeta,
  pricing,
  suiteImages,
  isRoomMode,
  isFixedMode,
  watch,
  setValue,
  availableClassNames,
  availableDurations,
  onClassChange,
  selectedWindow,
  classDurationMap,
  suiteProductIds,
  isSpecificFixedClassPage = false
}: LeftPaneMobileProps) {
  // ⭐ FIX 2 — Add this guard right here
  if (!classMeta || !classMeta.sections) {
    return <div className="p-4 text-gray-500 text-sm">Loading class information…</div>;
  }
  const classDisplayName = classMeta.displayName ?? classMeta.name;
  const [activeSection, setActiveSection] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);

  const sectionTitles = {
    pricing: "Pricing",
    schedule: "Schedule",
    groupSize: "Group Size",
    accommodationsUrl: "Accommodations",
    paymentUrl: "Payment",
    faqUrl: "FAQ",
    termsUrl: "Terms and Conditions",
    privacyUrl: "Privacy Policy",
  };

  const sectionsWithPricing = {
    pricing: pricing.pricing,
    ...classMeta.sections,
    // raw HTML string
  };
  const tabs = Object.keys(sectionsWithPricing).filter((key) => sectionTitles[key]);
  // Inject pricing into the sections map

  const rawClassName = watch("ExperienceTitle");

  const selectedClassName = rawClassName !== undefined ? rawClassName : availableClassNames[0];

  const selectedClassLength = watch("Experience");

  const threeDayClassNames = availableClassNames.filter((name) => classDurationMap?.[name] === "0");

  const fiveDayClassNames = availableClassNames.filter((name) => classDurationMap?.[name] === "1");

  useEffect(() => {
    if (!suiteImages || suiteImages.length === 0) return;

    let loadedCount = 0;

    suiteImages.forEach(({ src }) => {
      const img = new Image();
      img.onload = () => {
        loadedCount++;
        if (loadedCount === suiteImages.length) {
          setImagesLoaded(true);
        }
      };
      img.src = src;
    });
  }, [suiteImages]);

  return (
    <div className="flex flex-col w-full h-full overflow-hidden">
      {/* ⭐ FIXED TOP SECTION */}
      <div className="flex-shrink-0">
        {/* ROOM MODE TOP SECTION */}
        {isRoomMode ? (
          <div className="w-full mb-3 flex flex-row gap-3">
            {/* Suite Name (read-only) */}
            <div className="flex flex-col w-2/3">
              <label className="text-sm font-semibold text-gray-700">Suite Name</label>
              <select
                className="text-input text-sm w-full"
                value={selectedClassName}
                onChange={(e) => {
                  const newSuiteName = e.target.value;
                  setValue("ExperienceTitle", newSuiteName, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });

                  // find the suite object
                  const suite = suiteProductIds.find((s) => s.productName === newSuiteName);

                  // update productId so images + availability update
                  if (suite) {
                    setValue("productId", suite.productId, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }
                }}
              >
                {suiteProductIds.map((suite) => (
                  <option key={suite.productId} value={suite.productName}>
                    {suite.productName}
                  </option>
                ))}
              </select>
            </div>

            {/* # Guests */}
            <div className="flex flex-col w-1/3">
              <label className="text-sm font-semibold text-gray-700"># Guests</label>
              <select
                className="text-input text-sm w-full"
                value={watch("NumberofGuests")}
                onChange={(e) =>
                  setValue("NumberofGuests", e.target.value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
              >
                {[1, 2].map((num) => (
                  <option key={num} value={String(num)}>
                    {num}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <>
            {/* CLASS MODE TOP SECTION */}
            <div className="w-full mb-3 flex flex-row gap-3">
              {/* Class Length */}
              <div className="flex flex-col w-1/2">
                <label className="text-sm font-semibold text-gray-700">Class Length</label>
                <select
                  className="text-input text-sm w-full"
                  value={watch("Experience")}
                  onChange={(e) =>
                    setValue("Experience", e.target.value, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }
                >
                  <option value="">All Class Lengths</option>
                  {availableDurations.includes("0") && <option value="0">3-Day Class (4 nights)</option>}
                  {availableDurations.includes("1") && <option value="1">5-Day Class (6 nights)</option>}
                </select>
              </div>

              {/* Class Name */}
              <div className="flex flex-col w-1/2">
                <label className="text-sm font-semibold text-gray-700">Class Name</label>
                <select
                  className="text-input w-full text-sm"
                  value={selectedClassName}
                  onChange={(e) => {
                    const newClass = e.target.value;
                    if (newClass === "All Classes") setSelectedWindow?.(null);
                    setValue("ExperienceTitle", newClass, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    onClassChange?.(newClass);
                  }}
                >
                  {isFixedMode && !isSpecificFixedClassPage && <option value="">All Classes</option>}
                  {selectedClassLength === "" ? (
                    <>
                      {threeDayClassNames.length > 0 && (
                        <optgroup label="3-Day Classes">
                          {threeDayClassNames.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </optgroup>
                      )}

                      {fiveDayClassNames.length > 0 && (
                        <optgroup label="5-Day Classes">
                          {fiveDayClassNames.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </>
                  ) : (
                    availableClassNames.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>
          </>
        )}

        {/* IMAGE / SWIPER — works for both modes */}
        <div className="w-full flex justify-center mb-2">
          <div
            ref={rootRef}
            className="leftpane-root rounded-lg overflow-hidden bg-gray-200 relative"
            style={{
              width: isRoomMode ? "245px" : "205px",
              height: isRoomMode ? "245px" : "205px",
            }}
          >
            {suiteImages?.length > 0 ? (
              <Swiper
                modules={[Navigation, Pagination]}
                navigation
                pagination={{ clickable: true }}
                onInit={(swiper) => {
                  const slide = swiper.slides[swiper.activeIndex];
                  const img = slide.querySelector("img") as HTMLImageElement | null;
                  if (img) updateNavContrast(img, rootRef.current);
                }}
                onSlideChange={(swiper) => {
                  const slide = swiper.slides[swiper.activeIndex];
                  const img = slide.querySelector("img") as HTMLImageElement | null;
                  if (img) updateNavContrast(img, rootRef.current);
                }}
              >
                {suiteImages.map((img, i) => (
                  <SwiperSlide key={i}>
                    <div
                      className="relative group h-full w-full cursor-pointer zoom-hover"
                      onClick={() => {
                        setLightboxIndex(i);
                        setLightboxOpen(true);
                      }}
                    >
                      <img src={img.src} alt={img.alt || `Image ${i + 1}`} className="w-full h-full object-cover" />
                      <div className="zoom-overlay absolute bottom-1 right-1 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition">
                        Tap to zoom
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : classMeta?.imageUrl ? (
              <img src={classMeta.imageUrl} alt={classDisplayName} className="w-full h-full object-cover" />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">No Image</div>
            )}
          </div>
        </div>
      </div>

      {/* DESCRIPTION */}
      <div className="px-1">
        <div className="w-full flex flex-col mb-2">
          <label className="text-sm font-semibold text-gray-700 mb-1 block self-start">Class Description</label>

          <div
            className="bg-[#f7f0ea] border border-gray-300 rounded-lg p-3"
            style={{
              minHeight: "140px",
              maxHeight: "140px",
              overflowY: "auto", // scrolls just the beige box
              width: "100%",
            }}
          >
            <p className="text-gray-700 text-sm whitespace-pre-line leading-snug">{classMeta?.shortDescription}</p>
          </div>
        </div>
      </div>

      {/* ADDITIONAL INFO — sits right under description */}
      <div className="bg-white px-1  border-t border-gray-200">
        <label className="text-sm font-semibold text-gray-700 mb-1 block">Additional Info</label>

        <div className="flex flex-wrap gap-2 justify-center">
          {tabs.map((key) => (
            <button
              key={key}
              onClick={() => setActiveSection(key)}
              className="bg-[#f7f0ea] border border-gray-300 rounded px-3 py-1.5 text-sm font-medium whitespace-nowrap hover:bg-[#e8dcc7] transition"
            >
              {sectionTitles[key]}
            </button>
          ))}
        </div>
      </div>

      {/* MODAL */}
      {activeSection && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-center" onClick={() => setActiveSection(null)}>
          <div
            className="bg-white rounded-b-lg shadow-xl overflow-hidden flex flex-col"
            style={{
              width: "80%",
              maxWidth: "350px",
              height: "70%",
              maxHeight: "600px",
              marginTop: "5vh",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* HEADER */}
            <div className="p-4 border-b flex justify-between items-center flex-shrink-0">
              <h3 className="text-lg font-semibold">{sectionTitles[activeSection]}</h3>
              <button className="text-gray-500 hover:text-gray-800" onClick={() => setActiveSection(null)}>
                ✕
              </button>
            </div>

            {/* CONTENT */}
            <div className="flex-1 overflow-auto" style={{ WebkitOverflowScrolling: "touch" }}>
              {/* URL-based content */}
              {sectionsWithPricing[activeSection]?.startsWith?.("http") ? (
                <iframe src={sectionsWithPricing[activeSection]} className="w-full h-full border-0" />
              ) : (
                /* TEXT or PRICING HTML */
                <div
                  className="p-4 whitespace-pre-line text-sm text-gray-700"
                  dangerouslySetInnerHTML={
                    activeSection === "pricing"
                      ? {
                          __html: pricing.pricing.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>").replace(/__(.*?)__/g, "<u>$1</u>"),
                        }
                      : { __html: sectionsWithPricing[activeSection] }
                  }
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* LIGHTBOX */}
      {suiteImages?.length > 0 && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          index={lightboxIndex}
          slides={suiteImages.map((img) => ({ src: img.src }))}
        />
      )}
    </div>
  );
}
